import {getRequest} from '@/Services/ApiService';
import {reportError} from '@/Services/Logger';
import APIURL from '@/api-url';
import {dateConvertor} from '@/components/Common/dateconverter';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import {MUIAvatar, MUIBox, MUIContainer, MUIGrid, MUISpinner, MUITypography} from '@/components/MUI-Components';
import {BlogLinkRedirect} from '@/util/Blog-link';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import Typography from '@mui/material/Typography';
import {NextPageContext} from 'next';
import dynamic from 'next/dynamic';
import PageHead from '@/components/PageHead';
import Link from 'next/link';
import React, {memo, useEffect, useState} from 'react';
const DisplayImage = dynamic(() => import('@/components/Ui-components/display-image'));
import parse from 'html-react-parser';
import {addTargetBlank} from '@/util/html-parser-utils';
import {newsTableOptions} from '@/components/blog-shared/html-parser';
import {fixSchemaDomain, cleanFaqNodes} from '@/util/seo-utils';
import {proseLinkSx, colors, fontFamily, fontSize, fontWeight} from '@/styles/tokens';
import styles from '@/styles/newsDetail.module.scss';

// ─── Shared sub-components (extracted from this file + use-cases page) ────────
const ShareBar = dynamic(() => import('@/components/blog-shared/ShareBar'));
import Box from '@mui/material/Box';
import skelton from 'public/images/blog/skelton.webp';
import {setEdgeCache} from '@/util/edgeCache';

/** Generate up-to-2-letter initials from a display name */
const getInitials = (name: string): string => {
    const parts = (name || '').trim().split(/\s+/);
    if (parts.length === 0 || !parts[0]) return '?';
    return parts.length === 1 ? parts[0][0].toUpperCase() : (parts[0][0] + (parts.at(-1)?.[0] ?? '')).toUpperCase();
};

// ─── SEO & Schema Utilities ──────────────────────────────────────────────────
// Functions moved to @/util/seo-utils.ts for clean code.
// ─────────────────────────────────────────────────────────────────────────────

// ─── Module-level style constants ─────────────────────────────────────────────
const breadcrumbLinkBaseStyle: React.CSSProperties = {fontSize: '19px', color: '#551a8b'};
const breadcrumbLinkMrStyle: React.CSSProperties = {...breadcrumbLinkBaseStyle, marginRight: '4px'};
const breadcrumbLinkMxStyle: React.CSSProperties = {...breadcrumbLinkBaseStyle, margin: '0px 4px'};
const shareBoxSx = {display: 'flex', alignItems: 'center', gap: '10px'} as const;
const shareTextSx = {m: 0, fontSize: '20px'} as const;

// Migrated from globals.scss `.news-header` (sole consumer) + laptopScreen
// (max-width:1024px) + mobileScreen (max-width:430px) breakpoint overrides.
const newsHeaderSx = {
    fontFamily: fontFamily.heading,
    fontSize: '41px !important', // no exact tokens.ts match
    fontWeight: `${fontWeight.bold} !important`,
    color: `${colors.text.primary} !important`,
    lineHeight: '51px !important', // no exact tokens.ts match
    paddingRight: '10px',
    '@media (max-width: 1024px)': {paddingBottom: '30px !important'},
    '@media (max-width: 430px)': {fontSize: `${fontSize[30]} !important`, lineHeight: 'normal !important'}
} as const;

// Migrated from globals.scss `.blog-head-bottom` (sole consumer) + mobileScreen
// (max-width:430px) breakpoint override.
const blogHeadBottomSx = {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    flexWrap: 'wrap',
    '@media (max-width: 430px)': {justifyContent: 'center'}
} as const;

// Migrated from globals.scss `.blog-author` (sole consumer).
const blogAuthorSx = {borderRight: '1px solid #858585', paddingRight: '10px !important'} as const; // no exact tokens.ts color match

// Migrated from globals.scss `.time-text` (sole consumer).
const timeTextSx = {display: 'flex', justifyContent: 'center', gap: '5px'} as const;

// Migrated from globals.scss `.text` (sole consumer).
const blogMetaTextSx = {
    fontFamily: fontFamily.body,
    fontSize: `${fontSize[14]} !important`,
    fontWeight: `${fontWeight.regular} !important`,
    color: `${colors.text.secondary} !important`
} as const;

// Migrated from globals.scss `.blog-icon` (sole consumer).
const blogIconSx = {color: `${colors.text.secondary} !important`} as const;
// ─────────────────────────────────────────────────────────────────────────────

/** Author meta row (avatar + name + tooltip + date). */
const BlogAuthorMeta = memo(({blogData}: {blogData: any}) => (
    <MUIBox className=" blog-head-bottom" sx={{...blogHeadBottomSx, mt: 3}}>
        <MUIBox className="time-text blog-author" sx={{...timeTextSx, ...blogAuthorSx}}>
            <CalendarMonthIcon className="blog-icon" sx={blogIconSx} />
            <MUITypography className="text" sx={blogMetaTextSx}>
                {blogData ? dateConvertor(blogData.date) : '----'}
            </MUITypography>
        </MUIBox>
        <MUIBox className="time-text " sx={timeTextSx}>
            <AccessTimeIcon className="blog-icon" sx={blogIconSx} />
            <MUITypography className="text" sx={blogMetaTextSx}>
                20 MIN TO READ
            </MUITypography>
        </MUIBox>
    </MUIBox>
));
BlogAuthorMeta.displayName = 'BlogAuthorMeta';

// ─────────────────────────────────────────────────────────────────────────────

const BlogDetail = (props: any) => {
    const {blogData, id} = props;
    const [commentData, setCommentData] = useState<any>();
    const [loading, setLoading] = useState(false);

    // ── Extract TOC headings + fetch comments ──────────────────────────────
    useEffect(() => {
        if (!blogData) return;
        setLoading(true);

        if (!blogData.id) {
            setCommentData([]);
            setLoading(false);
            return;
        }

        getRequest(`${APIURL.COMMENT_LISTING}?post=${blogData.id}`)
            .then((res: any) => setCommentData(res))
            .catch(() => setCommentData([]))
            .finally(() => setLoading(false));
    }, [blogData]); // ← dep array fixed (was missing blogData)

    const shareUrl = `${blogData?.link}`;
    const yoastSchema = blogData?.yoast_head_json?.schema
        ? JSON.stringify(
              fixSchemaDomain({
                  ...blogData.yoast_head_json.schema,
                  '@graph': cleanFaqNodes(blogData.yoast_head_json.schema['@graph'])
              })
          ).replace(/\/blog\//g, '/news/')
        : undefined;
    return (
        <>
            <PageHead
                title={blogData?.yoast_head_json?.title}
                description={blogData?.yoast_head_json?.description}
                image={blogData?.news_image || blogData?.blog_image}
                canonical={blogData?.yoast_head_json?.og_url?.replace('/blog/', '/news/')}
                ogTitle={typeof blogData?.title === 'string' ? blogData.title : blogData?.title?.rendered}
                ogDescription={blogData?.meta?.description || blogData?.yoast_head_json?.description}
                ogLocale="en_US"
                schema={yoastSchema}
            />

            <MUIBox className={blogData?.title ? 'blog-detail-parent' : 'skeleton_div'} sx={{mb: 6}}>
                {blogData?.title && (
                    <MUIContainer sx={{maxWidth: '1150px !important'}}>
                        {id !== '' && (
                            <Box sx={{mt: 1, mb: 2}}>
                                <Typography variant="h6" className="remove-underline" component="p">
                                    <Link href="/" style={breadcrumbLinkMrStyle}>
                                        Home
                                    </Link>{' '}
                                    /{' '}
                                    <Link href="/news" style={breadcrumbLinkMxStyle}>
                                        News
                                    </Link>{' '}
                                </Typography>
                            </Box>
                        )}
                        <MUIBox sx={{textAlign: 'left', mb: 4}}>
                            <MUITypography className="news-header" component="h1" sx={{...newsHeaderSx, mt: 3, mb: 1, textAlign: 'left'}}>
                                {typeof blogData?.title === 'string' ? blogData.title : blogData?.title?.rendered}
                            </MUITypography>
                            <BlogAuthorMeta blogData={blogData} />
                        </MUIBox>

                        <MUIBox className="blog-image" sx={{mb: 1}}>
                            <DisplayImage
                                src={blogData.news_image || blogData.blog_image || skelton}
                                height={638}
                                width={1150}
                                alt={typeof blogData.title === 'string' ? blogData.title : blogData.title?.rendered}
                                quality={90}
                                priority={true}
                                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 100vw, 100vw"
                            />
                        </MUIBox>

                        <MUIBox className="tabel-blog-outer">
                            <MUIGrid container className="table-content-wrapper" spacing={4}>
                                <MUIGrid item xs={12} sm={12} md={12} className={styles.newsSlugContent} sx={proseLinkSx}>
                                    {blogData?.content && parse(addTargetBlank(blogData.content.rendered), newsTableOptions)}
                                </MUIGrid>
                            </MUIGrid>
                        </MUIBox>

                        <MUIBox className="blog-auth-large-wrapper" sx={shareBoxSx}>
                            <Typography className="font-headers" component="p" sx={shareTextSx}>
                                Share:{' '}
                            </Typography>{' '}
                            <ShareBar shareUrl={shareUrl} />
                        </MUIBox>

                        {/* Comments */}
                        {commentData &&
                            commentData.length >= 1 &&
                            commentData?.map((item: any) => (
                                <MUIGrid item xs={12} key={item.id}>
                                    <MUIGrid container className="top-spacing margin">
                                        <MUIGrid item xs={3} sm={1.5} md={1}>
                                            <MUIAvatar className="blog-avatar comment-user">{getInitials(item.author_name)}</MUIAvatar>
                                        </MUIGrid>
                                        <MUIGrid item xs={8} sm={9} md={10}>
                                            <MUIBox className="comment-user-name solutions left">
                                                <MUITypography className="spectrum-heading color-black">{item.author_name}</MUITypography>
                                                <MUITypography className="spectrum-sub-heading color-black">
                                                    {dateConvertor(item.date)}
                                                </MUITypography>
                                            </MUIBox>
                                            <MUIBox className="comment-content">
                                                <div className="comment-by-user">
                                                    {item?.content?.rendered ? parse(item.content.rendered) : ''}
                                                </div>
                                            </MUIBox>
                                        </MUIGrid>
                                    </MUIGrid>
                                </MUIGrid>
                            ))}
                    </MUIContainer>
                )}
            </MUIBox>

            {loading ? <MUISpinner /> : null}
        </>
    );
};

export default BlogDetail;
BlogDetail.Layout = 'News';

export const getServerSideProps: any = async (context: NextPageContext) => {
    const {query, res}: any = context;

    try {
        let redirectionOccurred = false;
        BlogLinkRedirect.forEach((item) => {
            let queryParams = '';
            if (query.hasOwnProperty('page')) {
                queryParams = `${query?.slug?.toLowerCase()}?page=${query?.page}`;
            } else if (query.hasOwnProperty('utm_source')) {
                queryParams = `${query?.slug?.toLowerCase()}?utm_source=${query?.utm_source?.toLowerCase()}`;
            } else {
                queryParams = query?.slug?.toLowerCase();
            }

            if (queryParams === item?.externalLink.toLowerCase()) {
                res.writeHead(301, {Location: `${item.redirect}`});
                res.end();
                redirectionOccurred = true;
            }
        });

        if (redirectionOccurred) return {props: {}};

        const response = await getRequest<any>(`${APIURL.NEWS_DETAILS}?slug=${query?.slug}`);
        const blogData = response[0] || null;
        // Slug genuinely does not exist in the CMS → real HTTP 404 (not a 200 skeleton).
        // This is a `return`, not a `throw`, so it never falls through to the 500 handler below.
        if (!blogData) return {notFound: true};

        let categoryResponse = null;
        let id = null;

        id = blogData?.categories && blogData.categories.length > 0 ? blogData.categories.at(-1) : null;

        if (id) {
            categoryResponse = await getRequest(
                `${APIURL.BLOGS_LISTING}?categories=${id}&per_page=4&_fields=id,title,slug,blog_image,date,featured_media`
            );
        }

        setEdgeCache(res);
        return {props: {blogData, query, id: null, categoryResponse: categoryResponse || null}};
    } catch (error) {
        // Reaching here means an infrastructure/transport failure (WordPress down, timeout, parse error) —
        // NOT "page missing". Returning notFound here would de-index live posts during an outage.
        // Emit HTTP 500 so Google retries instead of dropping the URL.
        reportError(error, {context: 'getServerSideProps:newsDetail'});
        if (res) res.statusCode = 500;
        return {props: {blogData: null, query, id: null, categoryResponse: null}};
    }
};
